/**
 * Psychrometric calculation utilities
 * Converted from VBA psychrocode.bas and basiscode.bas
 * ASHRAE 2009 standard psychrometric equations
 */

export interface PsychrometricState {
  dryBulbTemp: number      // 건구온도 (°C)
  wetBulbTemp: number      // 습구온도 (°C)
  relativeHumidity: number // 상대습도 (%)
  humidityRatio: number    // 절대습도 (kg/kg)
  enthalpy: number         // 엔탈피 (kJ/kg)
  dewPointTemp: number     // 이슬점온도 (°C)
}

export interface PsychrometricInputs {
  dryBulbTemp?: number
  wetBulbTemp?: number
  relativeHumidity?: number
  humidityRatio?: number
  enthalpy?: number
  altitude?: number
}

export class PsychrometricCalculator {
  private static readonly R_WATER_VAPOR = 0.621945 // Water vapor gas constant

  /**
   * Calculate pressure based on altitude (prefuc)
   * VBA: prefuc = 101.325 * (1 - 2.25577 * 10 ^ (-5) * Z) ^ 5.2559
   * VBA Single precision implementation with exact VBA constants
   */
  static calculatePressure(altitude: number): number {
    // VBA: prefuc = 101.325 * (1 - 2.25577 * 10 ^ (-5) * Z) ^ 5.2559
    return 101.325 * Math.pow(1 - 2.25577e-5 * altitude, 5.2559);
  }

  /**
   * Calculate saturation pressure (pwsfuc)
   * VBA exact implementation with Single precision
   * Function pwsfuc!(tdb!) / Dim t! / t = 273.15 + tdb
   */
  static calculateSaturationPressure(temperature: number): number {
    const t = 273.15 + temperature;
    
    if (temperature > 0) {
      // VBA: pwsfuc = (Exp(-5800.2206 / t + 1.3914993 - 0.048640239 * t + 0.000041764768 * t ^ 2 - 0.000000014452093 * t ^ 3 + 6.5459673 * Application.Ln(t))) / 1000
      return Math.exp(-5800.2206 / t + 1.3914993 - 0.048640239 * t + 0.000041764768 * t * t - 0.000000014452093 * t * t * t + 6.5459673 * Math.log(t)) / 1000;
    } else {
      // VBA: pwsfuc = (Exp(-5674.5359 / t + 6.3925247 - 0.009677843 * t + 0.00000062215701 * t ^ 2 + 2.0747825E-09 * t ^ 3 - 9.484024E-13 * t ^ 4 + 4.1635019 * Application.Ln(t))) / 1000
      return Math.exp(-5674.5359 / t + 6.3925247 - 0.009677843 * t + 0.00000062215701 * t * t + 2.0747825e-9 * t * t * t - 9.484024e-13 * t * t * t * t + 4.1635019 * Math.log(t)) / 1000;
    }
  }

  /**
   * Calculate humidity ratio from dry bulb temp and relative humidity (xfuc)
   */
  static calculateHumidityRatio(dryBulbTemp: number, relativeHumidity: number, altitude: number = 0): number {
    const pressure = this.calculatePressure(altitude);
    const saturationPressure = this.calculateSaturationPressure(dryBulbTemp);
    
    // VBA xfuc formula: 0.621945 * (rh * pws) / ((prefuc(Z) - rh * pws / 100) * 100)
    return 0.621945 * (relativeHumidity * saturationPressure) / ((pressure - relativeHumidity * saturationPressure / 100) * 100);
  }

  /**
   * Calculate humidity ratio with VBA Single precision (xfuc equivalent)
   * VBA의 Single 타입 정밀도를 정확히 모사
   */
  static calculateHumidityRatioVBA(dryBulbTemp: number, relativeHumidity: number, altitude: number = 0): number {
    // VBA Single precision 시뮬레이션
    const toSingle = (value: number): number => {
      const float32Array = new Float32Array(1);
      float32Array[0] = value;
      return float32Array[0];
    };

    // VBA: Dim pws! / pws = pwsfuc(tdb)
    const pws = toSingle(this.calculateSaturationPressure(toSingle(dryBulbTemp)));
    const pressure = toSingle(this.calculatePressure(toSingle(altitude)));
    const rh = toSingle(relativeHumidity);
    
    // VBA: xfuc = 0.621945 * (rh * pws) / ((prefuc(Z) - rh * pws / 100) * 100)
    const numerator = toSingle(toSingle(0.621945) * toSingle(rh * pws));
    const rhPws100 = toSingle(rh * pws / toSingle(100));
    const denominator = toSingle(toSingle(pressure - rhPws100) * toSingle(100));
    
    return toSingle(numerator / denominator);
  }

  /**
   * VBA Single precision calculate function
   * DB + RH 조합에서 VBA 정밀도 적용
   */
  static calculateVBA(dryBulbTemp: number, relativeHumidity: number, altitude: number = 0): PsychrometricState {
    const humidityRatio = this.calculateHumidityRatioVBA(dryBulbTemp, relativeHumidity, altitude);
    const enthalpy = this.calculateEnthalpy(dryBulbTemp, humidityRatio);
    const wetBulbTemp = this.calculateWetBulbTemp(dryBulbTemp, humidityRatio, altitude);
    const dewPointTemp = 0; // 임시로 0 설정

    return {
      dryBulbTemp,
      wetBulbTemp,
      relativeHumidity,
      humidityRatio,
      enthalpy,
      dewPointTemp
    };
  }

  /**
   * Calculate enthalpy (entfuc)
   * h = 1.006 * tdb + (2501 + (1.86 * tdb)) * x kJ/kg
   */
  static calculateEnthalpy(dryBulbTemp: number, humidityRatio: number): number {
    return 1.006 * dryBulbTemp + (2501 + 1.86 * dryBulbTemp) * humidityRatio
  }

  /**
   * Calculate humidity ratio from enthalpy (x1fuc)
   */
  static calculateHumidityRatioFromEnthalpy(dryBulbTemp: number, enthalpy: number): number {
    return (enthalpy - 1.006 * dryBulbTemp) / (2501 + 1.86 * dryBulbTemp)
  }

  /**
   * VBA Single precision float32 simulation
   * VBA uses 32-bit single precision floating point
   */
  static toSingle(value: number): number {
    // JavaScript에서 VBA Single 정밀도 시뮬레이션
    const float32Array = new Float32Array(1);
    float32Array[0] = value;
    return float32Array[0];
  }

  /**
   * Calculate relative humidity from dry bulb and humidity ratio (rhfuc)
   * VBA exact implementation with single precision simulation
   * VBA에서 모든 중간 계산이 Single precision으로 수행됨
   */
  static calculateRelativeHumidity(dryBulbTemp: number, humidityRatio: number, altitude: number = 0): number {
    // VBA Single precision 시뮬레이션
    const toSingle = (value: number): number => {
      const float32Array = new Float32Array(1);
      float32Array[0] = value;
      return float32Array[0];
    };

    // VBA: Dim pws! / pws = pwsfuc(tdb)
    const pws = toSingle(this.calculateSaturationPressure(toSingle(dryBulbTemp)));
    const pressure = toSingle(this.calculatePressure(toSingle(altitude)));
    const x = toSingle(humidityRatio);

    // VBA rhfuc formula: prefuc(Z) * x / (pws * (0.621945 + x)) * 100
    // VBA 계산 순서를 정확히 따라야 함 (좌에서 우로, Single precision)
    // 1. sum1 = 0.621945 + x
    const sum1 = toSingle(toSingle(0.621945) + x);
    // 2. prod1 = pws * sum1
    const prod1 = toSingle(pws * sum1);
    // 3. prod2 = prefuc(Z) * x
    const prod2 = toSingle(pressure * x);
    // 4. div1 = prod2 / prod1
    const div1 = toSingle(prod2 / prod1);
    // 5. result = div1 * 100
    const result = toSingle(div1 * toSingle(100));

    return result;
  }
  
  /**
   * VBA-style rounding function to match Application.Round behavior
   * VBA uses "banker's rounding" (round half to even)
   */
  static vbaRound(value: number, decimals: number): number {
    const factor = Math.pow(10, decimals)
    const shifted = value * factor
    
    // Check if we're exactly at 0.5 (within a small tolerance)
    const fractionalPart = shifted - Math.floor(shifted)
    const isExactlyHalf = Math.abs(fractionalPart - 0.5) < 1e-10
    
    if (isExactlyHalf) {
      // Banker's rounding: round to nearest even number
      const floor = Math.floor(shifted)
      const rounded = floor % 2 === 0 ? floor : floor + 1
      return rounded / factor
    } else {
      // Standard rounding for non-half values
      return Math.round(shifted) / factor
    }
  }
  

  /**
   * VBA-style round up function to match Application.RoundUp behavior
   * Always rounds up to the specified number of decimal places
   */
static vbaRoundUp(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  const sign = value >= 0 ? 1 : -1;
  return sign * Math.ceil(Math.abs(value) * factor) / factor;
}


  /**
   * Calculate dew point temperature (dewfuc)
   * VBA has two versions: dewfuc (ASHRAE 2009) and dew1fuc (RTS Origin)
   */
  static calculateDewPoint(vaporPressure: number): number {
    if (vaporPressure <= 0) return -273.15
    
    // Using VBA dewfuc (ASHRAE 2009) formula
    const Y = Math.log(vaporPressure) // Note: VBA uses kPa directly, not Pa
    
    if (vaporPressure >= 0.6112) {
      // VBA dewfuc formula for pw >= 0.6112 kPa
      return 6.54 + 14.526 * Y + 0.7389 * Y * Y + 0.09486 * Y * Y * Y + 0.4569 * Math.pow(vaporPressure, 0.1984)
    } else {
      // VBA dewfuc formula for pw < 0.6112 kPa  
      return 6.09 + 12.608 * Y + 0.4959 * Y * Y
    }
  }
  
  /**
   * Alternative dew point calculation (dew1fuc - RTS Origin)
   * VBA dew1fuc function for comparison
   */
  static calculateDewPointRTS(vaporPressure: number): number {
    if (vaporPressure <= 0) return -273.15
    
    // Convert kPa to Pa as in VBA (pwx = pw * 1000)
    const pwx = vaporPressure * 1000
    const Y = Math.log(pwx)
    
    if (pwx >= 611.2) {
      return -77.199 + Y * (13.198 + Y * (-0.63772 + 0.071098 * Y))
    } else {
      return -60.662 + Y * (7.4624 + Y * (0.20594 + 0.016321 * Y))
    }
  }

  /**
   * Calculate wet bulb temperature (wbfuc)
   * VBA wbfuc algorithm with dewfuc and pwxfuc
   */
  static calculateWetBulbTemp(dryBulbTemp: number, humidityRatio: number, altitude: number = 0): number {
    // VBA wbfuc algorithm
    // tdew = dewfuc(pwxfuc(x, Z))
    const vaporPressure = this.calculateVaporPressureFromHumidityRatio(humidityRatio, altitude)
    const dewPoint = this.calculateDewPoint(vaporPressure)
    
    let twb1 = dryBulbTemp
    let twb2 = dewPoint
    let twb = (twb1 + twb2) / 2
    
    // VBA iteration: Do While twb1 - twb2 > 0.001
    while (twb1 - twb2 > 0.001) {
      const x1 = this.calculateHumidityRatioFromWetBulb(dryBulbTemp, twb, altitude)
      
      if (x1 > humidityRatio) {
        twb1 = twb
      } else {
        twb2 = twb
      }
      twb = (twb1 + twb2) / 2
    }
    
    return twb
  }

  /**
   * Calculate humidity ratio from wet bulb temperature (x3fuc)
   * VBA x3fuc formula with ent1fuc and x2fuc
   */
  private static calculateHumidityRatioFromWetBulb(dryBulbTemp: number, wetBulbTemp: number, altitude: number = 0): number {
    // VBA ent1fuc function
    const h = wetBulbTemp > 0 ? wetBulbTemp * 4.186 : -333.4 + 2.1 * wetBulbTemp
    
    // VBA x2fuc function: x2fuc = 0.621945 * pw / (prefuc(Z) - pw)
    const pressure = this.calculatePressure(altitude)
    const saturationPressureWb = this.calculateSaturationPressure(wetBulbTemp)
    const x2 = 0.621945 * saturationPressureWb / (pressure - saturationPressureWb)
    
    // VBA x3fuc formula
    return ((2501 + 1.86 * wetBulbTemp - h) * x2 - 1.006 * (dryBulbTemp - wetBulbTemp)) / 
           (1.86 * dryBulbTemp + 2501 - h)
  }
  
  /**
   * Calculate vapor pressure from humidity ratio (pwxfuc)
   * VBA pwxfuc formula
   */
  private static calculateVaporPressureFromHumidityRatio(humidityRatio: number, altitude: number = 0): number {
    const pressure = this.calculatePressure(altitude)
    return humidityRatio * pressure / (humidityRatio + 0.621945)
  }

  /**
   * Find dry bulb temperature from humidity ratio and relative humidity (inverse of calculateHumidityRatio)
   * Uses iterative approach to find the temperature
   */
  static findDryBulbTempFromHumidityRatio(targetHumidityRatio: number, relativeHumidity: number, altitude: number = 0): number {
    if (relativeHumidity <= 0) throw new Error("Relative humidity must be positive.");

    let minTemp = -100;
    let maxTemp = 200;
    let currentTemp = 50; // Initial guess
    let calculatedHumidityRatio;
    
    for (let i = 0; i < 30; i++) { // Limit iterations to prevent infinite loops
      calculatedHumidityRatio = this.calculateHumidityRatio(currentTemp, relativeHumidity, altitude);
      
      if (Math.abs(calculatedHumidityRatio - targetHumidityRatio) < 1e-6) {
        return currentTemp; // Found a close enough value
      }
      
      if (calculatedHumidityRatio < targetHumidityRatio) {
        minTemp = currentTemp;
      } else {
        maxTemp = currentTemp;
      }
      currentTemp = (minTemp + maxTemp) / 2;
    }
    
    return currentTemp; // Return best guess after iterations
  }

  /**
   * Main psychrometric calculation function
   * Determines all psychrometric properties from two known properties
   */
  static calculate(inputs: PsychrometricInputs): PsychrometricState | null {
    const { dryBulbTemp, wetBulbTemp, relativeHumidity, humidityRatio, enthalpy, altitude = 0 } = inputs
    
    try {
      let state: Partial<PsychrometricState> = {}

      // Determine calculation path based on available inputs
      if (dryBulbTemp !== undefined && relativeHumidity !== undefined) {
        // DB + RH
        state.dryBulbTemp = dryBulbTemp
        state.relativeHumidity = relativeHumidity
        state.humidityRatio = this.calculateHumidityRatio(dryBulbTemp, relativeHumidity, altitude)
        state.enthalpy = this.calculateEnthalpy(dryBulbTemp, state.humidityRatio)
        state.wetBulbTemp = this.calculateWetBulbTemp(dryBulbTemp, state.humidityRatio, altitude)
      } else if (dryBulbTemp !== undefined && humidityRatio !== undefined) {
        // DB + X
        state.dryBulbTemp = dryBulbTemp
        state.humidityRatio = humidityRatio
        state.enthalpy = this.calculateEnthalpy(dryBulbTemp, humidityRatio)
        state.relativeHumidity = this.calculateRelativeHumidity(dryBulbTemp, humidityRatio, altitude)
        state.wetBulbTemp = this.calculateWetBulbTemp(dryBulbTemp, humidityRatio, altitude)
      } else if (dryBulbTemp !== undefined && enthalpy !== undefined) {
        // DB + H
        state.dryBulbTemp = dryBulbTemp
        state.enthalpy = enthalpy
        state.humidityRatio = this.calculateHumidityRatioFromEnthalpy(dryBulbTemp, enthalpy)
        state.relativeHumidity = this.calculateRelativeHumidity(dryBulbTemp, state.humidityRatio, altitude)
        state.wetBulbTemp = this.calculateWetBulbTemp(dryBulbTemp, state.humidityRatio, altitude)
      } else if (dryBulbTemp !== undefined && wetBulbTemp !== undefined) {
        // DB + WB
        state.dryBulbTemp = dryBulbTemp
        state.wetBulbTemp = wetBulbTemp
        state.humidityRatio = this.calculateHumidityRatioFromWetBulb(dryBulbTemp, wetBulbTemp, altitude)
        state.relativeHumidity = this.calculateRelativeHumidity(dryBulbTemp, state.humidityRatio, altitude)
        state.enthalpy = this.calculateEnthalpy(dryBulbTemp, state.humidityRatio)
      } else {
        return null // Insufficient inputs
      }

      // Calculate dew point (using VBA dewfuc method with pwxfuc)
      const vaporPressure = this.calculateVaporPressureFromHumidityRatio(state.humidityRatio!, altitude)
      state.dewPointTemp = this.calculateDewPoint(vaporPressure)

      // Validate results (similar to VBA validation)
      if (state.relativeHumidity! > 100 || state.relativeHumidity! < 0 ||
          state.humidityRatio! < 0 || state.humidityRatio! > 0.16 ||
          state.dryBulbTemp! > 60 || state.dryBulbTemp! < -20 ||
          state.enthalpy! > 460 || state.wetBulbTemp! > 60 || state.wetBulbTemp! < -22) {
        return null
      }

      return state as PsychrometricState
    } catch (_error) {
      return null
    }
  }

  /**
   * Format result for display (similar to VBA rounding)
   */
  static formatResults(state: PsychrometricState): PsychrometricState {
    return {
      dryBulbTemp: Math.round(state.dryBulbTemp * 100) / 100,
      relativeHumidity: Math.round(state.relativeHumidity * 100) / 100,
      humidityRatio: Math.round(state.humidityRatio * 100000) / 100000,
      enthalpy: Math.round(state.enthalpy * 100) / 100,
      wetBulbTemp: Math.round(state.wetBulbTemp * 100) / 100,
      dewPointTemp: Math.round(state.dewPointTemp * 100) / 100
    }
  }
}